import type { WorkspaceGraphBundle, WorkspaceGraphEdge, WorkspaceGraphNode } from '../../services/kpWorkspaceService';
import {
  RELATED_GRAPH_SIDE_STRIPS_EXTENSION_WIDTH,
  WORKSPACE_PARTICLE_CARD_WIDTH,
} from '../../components/GraphParticleTicketChrome';

export const GRAPH_PADDING = 32;
export const GRAPH_COLUMN_GAP = 96;
export const GRAPH_ROW_GAP = 24;

export type GraphLayoutNodeMeta = {
  node: WorkspaceGraphNode;
  x: number;
  y: number;
  width: number;
  height: number;
  level: number;
  isRoot: boolean;
};

function nodeSortWeight(node: WorkspaceGraphNode): number {
  if (node.kind === 'asset') return 0;
  if (node.kind === 'particle') return 1;
  return 2;
}

function sortNodes(left: WorkspaceGraphNode, right: WorkspaceGraphNode): number {
  const weightDiff = nodeSortWeight(left) - nodeSortWeight(right);
  if (weightDiff !== 0) return weightDiff;
  return String(left.title || '').localeCompare(String(right.title || ''), 'zh-CN');
}

export function estimateGraphNodeSize(
  node: WorkspaceGraphNode,
  isRoot: boolean,
  options?: { papStrip?: boolean },
): { width: number; height: number } {
  if (node.kind === 'asset') {
    return { width: isRoot ? 272 : 248, height: isRoot ? 176 : 148 };
  }
  if (node.kind === 'particle') {
    const width = options?.papStrip
      ? WORKSPACE_PARTICLE_CARD_WIDTH + RELATED_GRAPH_SIDE_STRIPS_EXTENSION_WIDTH
      : WORKSPACE_PARTICLE_CARD_WIDTH;
    return { width, height: 208 };
  }
  return { width: 288, height: 144 };
}

export function resolveGraphRootNode(
  bundle: WorkspaceGraphBundle,
  assetId?: string,
  particleId?: string,
): WorkspaceGraphNode | null {
  if (assetId) {
    const explicit = bundle.nodes.find(
      (node) => node.kind === 'asset'
        && (node.assetId === assetId || node.id === `asset:${assetId}`),
    );
    if (explicit) return explicit;
  }
  if (particleId) {
    const explicit = bundle.nodes.find(
      (node) => (node.kind === 'particle' || node.kind === 'derived')
        && (node.particleId === particleId || node.id === `particle:${particleId}`),
    );
    if (explicit) return explicit;
  }
  return bundle.nodes.find((node) => node.kind === 'asset')
    || bundle.nodes.find((node) => node.kind === 'particle')
    || bundle.nodes[0]
    || null;
}

export function buildWorkspaceGraphLayout(
  bundle: WorkspaceGraphBundle,
  rootNode: WorkspaceGraphNode | null,
  options?: { papStripNodeIds?: Set<string> },
): { layoutNodes: GraphLayoutNodeMeta[]; layoutEdges: WorkspaceGraphEdge[] } {
  if (!bundle.nodes.length || !rootNode) {
    return { layoutNodes: [], layoutEdges: [] };
  }

  const papStripNodeIds = options?.papStripNodeIds || new Set<string>();
  const sizeOf = (node: WorkspaceGraphNode, isRoot: boolean) => estimateGraphNodeSize(
    node,
    isRoot,
    { papStrip: papStripNodeIds.has(node.id) },
  );

  const nodeById = new Map(bundle.nodes.map((node) => [node.id, node]));
  const adjacency = new Map<string, Set<string>>();
  for (const node of bundle.nodes) adjacency.set(node.id, new Set());
  for (const edge of bundle.edges) {
    adjacency.get(edge.source)?.add(edge.target);
    adjacency.get(edge.target)?.add(edge.source);
  }

  const levelByNodeId = new Map<string, number>();
  const queue = [rootNode.id];
  levelByNodeId.set(rootNode.id, 0);
  while (queue.length) {
    const currentId = queue.shift();
    if (!currentId) continue;
    const currentLevel = levelByNodeId.get(currentId) || 0;
    const neighbors = Array.from(adjacency.get(currentId) || []).sort((leftId, rightId) => {
      const leftNode = nodeById.get(leftId);
      const rightNode = nodeById.get(rightId);
      if (!leftNode || !rightNode) return 0;
      return sortNodes(leftNode, rightNode);
    });
    for (const neighborId of neighbors) {
      if (levelByNodeId.has(neighborId)) continue;
      levelByNodeId.set(neighborId, currentLevel + 1);
      queue.push(neighborId);
    }
  }

  let disconnectedLevel = Math.max(...Array.from(levelByNodeId.values()), 0) + 1;
  for (const node of [...bundle.nodes].sort(sortNodes)) {
    if (levelByNodeId.has(node.id)) continue;
    levelByNodeId.set(node.id, disconnectedLevel);
    disconnectedLevel += 1;
  }

  const grouped = new Map<number, WorkspaceGraphNode[]>();
  for (const node of bundle.nodes) {
    const level = levelByNodeId.get(node.id) || 0;
    if (!grouped.has(level)) grouped.set(level, []);
    grouped.get(level)?.push(node);
  }

  const orderedLevels = Array.from(grouped.keys()).sort((left, right) => left - right);
  const columnWidth = Math.max(
    ...orderedLevels.flatMap((level) => (
      (grouped.get(level) || []).map((node) => sizeOf(node, node.id === rootNode.id).width)
    )),
    248,
  );

  const estimatedColumnHeights = orderedLevels.map((level) => {
    const nodes = [...(grouped.get(level) || [])].sort(sortNodes);
    return nodes.reduce((height, node, index) => {
      const size = sizeOf(node, node.id === rootNode.id);
      return height + size.height + (index > 0 ? GRAPH_ROW_GAP : 0);
    }, 0);
  });
  const canvasHeight = Math.max(480, Math.max(...estimatedColumnHeights, 0) + GRAPH_PADDING * 2);

  const layoutNodes: GraphLayoutNodeMeta[] = [];
  for (const [columnIndex, level] of orderedLevels.entries()) {
    const nodes = [...(grouped.get(level) || [])].sort(sortNodes);
    const totalHeight = nodes.reduce((height, node, index) => {
      const size = sizeOf(node, node.id === rootNode.id);
      return height + size.height + (index > 0 ? GRAPH_ROW_GAP : 0);
    }, 0);
    let cursorY = GRAPH_PADDING + (canvasHeight - GRAPH_PADDING * 2 - totalHeight) / 2;
    const columnX = GRAPH_PADDING + columnIndex * (columnWidth + GRAPH_COLUMN_GAP);

    for (const node of nodes) {
      const isRoot = node.id === rootNode.id;
      const size = sizeOf(node, isRoot);
      layoutNodes.push({
        node,
        x: columnX + (columnWidth - size.width) / 2,
        y: cursorY,
        width: size.width,
        height: size.height,
        level,
        isRoot,
      });
      cursorY += size.height + GRAPH_ROW_GAP;
    }
  }

  const layoutNodeIds = new Set(layoutNodes.map((item) => item.node.id));
  const layoutEdges = bundle.edges.filter(
    (edge) => layoutNodeIds.has(edge.source) && layoutNodeIds.has(edge.target),
  );

  return { layoutNodes, layoutEdges };
}
